/**
 * Offline Sync Service
 * Handles automatic synchronization of offline ticket resolutions
 */

import offlineStorage from './offlineStorage';
import axiosInstance from '../utils/axios';
import { endpoints } from '../configs/api';
import indexedDBService from './indexedDBService';
import { getAppCheckToken } from '../configs/AppCheck';

// Escalated statuses — ticket has expired/been taken over, resolution should not be submitted
const ESCALATED_STATUSES = ['ESCALATED', 'LEVEL_1_ESCALATION', 'LEVEL_2_ESCALATION'];

/**
 * Build auth + app-check headers shared by all sync requests
 * @returns {Promise<Object>}
 */
async function buildHeaders(contentType = 'application/json') {
    const headers = { 'Content-Type': contentType };

    const authData = localStorage.getItem('auth');
    if (authData) {
        try {
            const auth = JSON.parse(authData);
            if (auth?.token) {
                headers.Authorization = `Bearer ${auth.token}`;
            }
        } catch (error) {
            console.error('Failed to parse auth from localStorage:', error);
        }
    }

    if (process.env.REACT_APP_ENV === 'staging' || process.env.REACT_APP_ENV === 'prod') {
        const appCheckToken = await getAppCheckToken();
        if (appCheckToken) {
            headers['x-oneqr-app-token'] = appCheckToken.token;
        }
    }

    return headers;
}

/**
 * Fetch the current status of a ticket from the server.
 * Returns null if the fetch fails — callers treat null as "safe to proceed".
 * @param {string} ticketId
 * @returns {Promise<string|null>}
 */
async function fetchTicketStatus(ticketId) {
    try {
        const headers = await buildHeaders();
        const response = await axiosInstance.post(
            endpoints.ticket.findMultiple,
            { id: ticketId },
            { headers }
        );

        if (response.status === 200 && response.data) {
            // findMultiple returns an array; pick the first match
            const ticket = Array.isArray(response.data) ? response.data[0] : response.data;
            return ticket?.status || null;
        }
        return null;
    } catch (error) {
        console.error(`Failed to fetch ticket status for ${ticketId}:`, error);
        return null;
    }
}

/**
 * Upload attachments and mark the ticket as RESOLVED on the server.
 * @param {Object} resolution
 * @returns {Promise}
 */
async function submitResolution(resolution) {
    const formData = new FormData();
    formData.append('ticket_id', resolution.ticketId);

    if (resolution.rootCause) formData.append('root_cause', resolution.rootCause);
    if (resolution.ca) formData.append('ca', resolution.ca);
    if (resolution.pa) formData.append('pa', resolution.pa);
    if (resolution.capa) formData.append('capa', resolution.capa);

    // Retrieve attachments stored in IndexedDB
    const attachments = await indexedDBService.getAttachments(resolution.id);
    console.log(`Retrieved ${attachments.length} attachments from IndexedDB for resolution ${resolution.id}`);

    attachments.forEach((attachment) => {
        const fileType = attachment.type.split('/')[0];
        if (fileType === 'image') {
            formData.append('image[]', attachment.blob, attachment.name);
        } else if (fileType === 'video') {
            formData.append('video[]', attachment.blob, attachment.name);
        } else if (fileType === 'audio') {
            formData.append('audio[]', attachment.blob, attachment.name);
        }
    });

    // Step 1: upload attachments
    const uploadHeaders = await buildHeaders('multipart/form-data');
    const uploadResponse = await axiosInstance.post(
        endpoints.ticket.uploadAttachments,
        formData,
        { headers: uploadHeaders }
    );

    if (uploadResponse.status !== 200 && uploadResponse.status !== 201) {
        const error = new Error(`Unexpected upload response status: ${uploadResponse.status}`);
        offlineStorage.markAsFailed(resolution.id, error.message);
        throw error;
    }

    // Step 2: update ticket status to RESOLVED
    const updateHeaders = await buildHeaders();
    const updateResponse = await axiosInstance.put(
        endpoints.ticket.update,
        { id: resolution.ticketId, status: 'RESOLVED', assignee_id: resolution.assigneeId },
        { headers: updateHeaders }
    );

    if (updateResponse.status !== 200) {
        const error = new Error(`Unexpected update response status: ${updateResponse.status}`);
        offlineStorage.markAsFailed(resolution.id, error.message);
        throw error;
    }

    offlineStorage.markAsSynced(resolution.id);
    await indexedDBService.deleteAttachments(resolution.id);
    console.log(`Resolution ${resolution.id} synced successfully and attachments cleaned up`);

    return updateResponse;
}

// -----------------------------------------------

class OfflineSyncService {
    constructor() {
        this.isSyncing = false;
        this.syncListeners = [];
        this.setupNetworkListeners();
    }

    /**
     * Setup network event listeners
     */
    setupNetworkListeners() {
        window.addEventListener('online', () => {
            console.log('Network online - triggering sync');
            this.syncPendingResolutions();
        });
    }

    /**
     * Add listener for sync events
     * @param {Function} callback
     */
    addSyncListener(callback) {
        this.syncListeners.push(callback);
    }

    /**
     * Remove sync listener
     * @param {Function} callback
     */
    removeSyncListener(callback) {
        this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
    }

    /**
     * Notify all listeners of sync events
     * @param {Object} event
     */
    notifyListeners(event) {
        console.log(`[notifyListeners] event: ${event.type}, listeners: ${this.syncListeners.length}`);
        this.syncListeners.forEach(callback => {
            try {
                callback(event);
            } catch (error) {
                console.error('Error in sync listener:', error);
            }
        });
    }

    /**
     * Sync all pending resolutions
     * @returns {Promise<Object>}
     */
    async syncPendingResolutions() {
        if (this.isSyncing) {
            console.log('Sync already in progress');
            return { success: false, message: 'Sync already in progress' };
        }

        if (!navigator.onLine) {
            console.log('Cannot sync - offline');
            return { success: false, message: 'Device is offline' };
        }

        this.isSyncing = true;
        this.notifyListeners({ type: 'sync_started' });

        const pendingResolutions = offlineStorage.getPendingResolutions()
            .filter(r => r.status === 'pending');

        if (pendingResolutions.length === 0) {
            this.isSyncing = false;
            this.notifyListeners({ type: 'sync_completed', results: { synced: 0, failed: 0, discarded: 0 } });
            return { success: true, synced: 0, failed: 0, discarded: 0 };
        }

        console.log(`Syncing ${pendingResolutions.length} pending resolutions`);

        const results = { synced: 0, failed: 0, discarded: 0, errors: [] };

        // Process sequentially to avoid overwhelming the server
        const processResolution = async (resolution) => {
            try {
                const result = await this.syncResolution(resolution);
                if (result?.discarded) {
                    results.discarded += 1;
                } else {
                    results.synced += 1;
                }
                this.notifyListeners({
                    type: result?.discarded ? 'resolution_discarded' : 'resolution_synced',
                    resolution,
                    progress: {
                        current: results.synced + results.failed + results.discarded,
                        total: pendingResolutions.length,
                    },
                });
            } catch (error) {
                results.failed += 1;
                results.errors.push({ resolutionId: resolution.id, error: error.message });
                this.notifyListeners({
                    type: 'resolution_failed',
                    resolution,
                    error: error.message,
                    progress: {
                        current: results.synced + results.failed + results.discarded,
                        total: pendingResolutions.length,
                    },
                });
            }
        };

        await pendingResolutions.reduce(async (prev, resolution) => {
            await prev;
            return processResolution(resolution);
        }, Promise.resolve());

        this.isSyncing = false;
        this.lastSyncResults = results;
        this.notifyListeners({ type: 'sync_completed', results });

        // Dispatch a DOM event so any mounted component can react,
        // even if it registered before React listeners were set up
        window.dispatchEvent(new CustomEvent('offlineSyncCompleted', { detail: results }));

        offlineStorage.cleanupSyncedResolutions(7);

        console.log('Sync completed:', results);
        return { success: true, ...results };
    }

    /**
     * Sync a single resolution.
     * Fetches the current ticket status first — discards the resolution if the ticket
     * was escalated or already resolved before we came back online.
     * @param {Object} resolution
     * @returns {Promise<Object>}
     */
    async syncResolution(resolution) {
        const currentStatus = await fetchTicketStatus(resolution.ticketId);

        if (currentStatus && ESCALATED_STATUSES.includes(currentStatus)) {
            const reason = `Ticket was escalated (status: ${currentStatus}) before sync could complete`;
            offlineStorage.markAsDiscarded(resolution.id, reason);
            await indexedDBService.deleteAttachments(resolution.id);
            console.warn(`Resolution ${resolution.id} discarded — ticket ${resolution.ticketId} is ${currentStatus}`);
            console.log('[syncResolution] notifying listeners with resolution_discarded');
            this.notifyListeners({ type: 'resolution_discarded', resolution, reason, ticketStatus: currentStatus });
            return { discarded: true };
        }

        if (currentStatus === 'RESOLVED') {
            const reason = 'Ticket was already resolved before sync could complete';
            offlineStorage.markAsDiscarded(resolution.id, reason);
            await indexedDBService.deleteAttachments(resolution.id);
            console.warn(`Resolution ${resolution.id} discarded — ticket ${resolution.ticketId} already RESOLVED`);
            console.log('[syncResolution] notifying listeners with resolution_discarded');
            this.notifyListeners({ type: 'resolution_discarded', resolution, reason, ticketStatus: currentStatus });
            return { discarded: true };
        }

        // Status is safe (WORK_IN_PROGRESS, ACKNOWLEDGE, CREATED) or fetch failed — proceed
        try {
            return await submitResolution(resolution);
        } catch (error) {
            console.error(`Failed to sync resolution ${resolution.id}:`, error);
            offlineStorage.markAsFailed(resolution.id, error.message);
            throw error;
        }
    }

    /**
     * Manually trigger sync
     * @returns {Promise<Object>}
     */
    async manualSync() {
        return this.syncPendingResolutions();
    }

    /**
     * Get sync status
     * @returns {Object}
     */
    getSyncStatus() {
        return {
            isSyncing: this.isSyncing,
            ...offlineStorage.getSyncStatus(),
        };
    }

    /**
     * Get the results of the last completed sync, then clear them.
     * Used by pages that mount after the sync has already fired.
     * @returns {Object|null}
     */
    consumeLastSyncResults() {
        const results = this.lastSyncResults || null;
        this.lastSyncResults = null;
        return results;
    }
}

export default new OfflineSyncService();
